// Unified LLM Interface Base

/**
 * Create unified LLM interface functions
 * @param {Object} providers - Provider adapters (openai, gemini, ollama)
 * @returns {Object} Interface functions
 */
export function createUnifiedInterface(providers) {
  const providerNames = Object.keys(providers)

  /**
   * Unified chat interface
   */
  const chat = async ({ provider, apiKey, model, messages, tools, config = {}, ...options }) => {
    const providerImpl = providers[provider]
    if (!providerImpl) {
      throw new Error(
        `Provider ${provider} not supported. Available: ${providerNames.join(', ')}`,
      )
    }

    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    try {
      return await providerImpl.chat({ apiKey, model, messages, tools, config, ...options })
    } catch (error) {
      throw new Error(`[${provider}] Chat failed: ${error.message}`, { cause: error })
    }
  }

  /**
   * List available models for a provider
   */
  const listModels = async ({ provider, apiKey }) => {
    const providerImpl = providers[provider]
    if (!providerImpl) {
      throw new Error(`Provider ${provider} not supported`)
    }
    return await providerImpl.listModels({ apiKey })
  }

  /**
   * Single step chat - calls LLM once, returns tool calls if needed (does not execute tools)
   * @param {Object} params
   * @param {string} params.provider - LLM provider
   * @param {string} params.apiKey - API key
   * @param {string} params.model - Model name
   * @param {Array} params.messages - Conversation messages
   * @param {Array} params.tools - Tool definitions (optional)
   * @param {Object} params.config - LLM configuration (optional)
   * @returns {Object} { status: 'needs_tool_call' | 'complete', answer, toolCalls, usage }
   */
  const chatSingleStep = async ({ provider, apiKey, model, messages, tools, config = {} }) => {
    const response = await chat({ provider, apiKey, model, messages, tools, config })
    const { message } = response

    if (!message.toolCalls || message.toolCalls.length === 0) {
      return {
        status: 'complete',
        answer: message.content,
        toolCalls: null,
        usage: message.usage,
        raw: response.raw,
      }
    }

    return {
      status: 'needs_tool_call',
      answer: message.content,
      toolCalls: message.toolCalls,
      usage: message.usage,
      raw: response.raw,
    }
  }

  /**
   * Execute tool and continue conversation
   */
  const chatWithTools = async ({
    ctx,
    provider,
    apiKey,
    model,
    messages,
    tools,
    toolImplementations,
    config = {},
    maxIterations = 5,
  }) => {
    let currentMessages = [...messages]
    let iteration = 0

    while (iteration < maxIterations) {
      iteration++

      const response = await chat({
        provider,
        apiKey,
        model,
        messages: currentMessages,
        tools,
        config,
      })

      const { message } = response

      if (!message.toolCalls || message.toolCalls.length === 0) {
        return {
          message,
          messages: currentMessages,
          iterations: iteration,
          raw: response.raw,
        }
      }

      currentMessages.push({
        role: 'assistant',
        content: message.content || null,
        toolCalls: message.toolCalls,
      })

      for (const toolCall of message.toolCalls) {
        const toolName = toolCall.function.name
        const toolImpl = toolImplementations[toolName]

        if (!toolImpl) {
          throw new Error(`Tool implementation not found: ${toolName}`)
        }

        const args =
          typeof toolCall.function.arguments === 'string'
            ? JSON.parse(toolCall.function.arguments)
            : toolCall.function.arguments
        const result = await toolImpl(ctx, args)

        currentMessages.push({
          role: 'tool',
          toolCallId: toolCall.id,
          toolName: toolName,
          content: JSON.stringify(result),
        })
      }

      if (provider === 'gemini') {
        currentMessages.push({
          role: 'user',
          content: 'Please provide the answer based on the function results.',
        })
      }
    }

    throw new Error(`Max iterations (${maxIterations}) reached without final answer`)
  }

  /**
   * Execute natural language command using available tools
   */
  const execute = async ({
    ctx,
    provider,
    apiKey,
    model,
    prompt,
    tools,
    toolImplementations,
    conversationHistory = [],
    config = {},
  }) => {
    const messages = [...conversationHistory, { role: 'user', content: prompt }]

    const result = await chatWithTools({
      ctx,
      provider,
      apiKey,
      model,
      messages,
      tools,
      toolImplementations,
      config,
      maxIterations: config.maxIterations || 5,
    })

    const toolsUsed = []
    for (const msg of result.messages) {
      if (msg.role === 'assistant' && msg.toolCalls) {
        msg.toolCalls.forEach((tc) => {
          if (!toolsUsed.includes(tc.function.name)) {
            toolsUsed.push(tc.function.name)
          }
        })
      }
    }

    return {
      answer: result.message.content,
      messages: result.messages,
      iterations: result.iterations,
      toolsUsed,
      usage: result.message.usage,
    }
  }

  /**
   * Create a conversational agent with persistent context
   */
  const createAgent = ({
    ctx,
    provider,
    apiKey,
    model,
    systemPrompt,
    tools,
    toolImplementations,
    config = {},
  }) => {
    const conversationHistory = []

    if (systemPrompt) {
      conversationHistory.push({ role: 'system', content: systemPrompt })
    }

    return {
      async chat(prompt) {
        const result = await execute({
          ctx,
          provider,
          apiKey,
          model,
          prompt,
          tools,
          toolImplementations,
          conversationHistory,
          config,
        })

        conversationHistory.push(...result.messages.slice(conversationHistory.length))

        return {
          answer: result.answer,
          toolsUsed: result.toolsUsed,
          iterations: result.iterations,
          usage: result.usage,
        }
      },

      getHistory() {
        return [...conversationHistory]
      },

      clearHistory() {
        const systemMessages = conversationHistory.filter((msg) => msg.role === 'system')
        conversationHistory.length = 0
        conversationHistory.push(...systemMessages)
      },
    }
  }

  return {
    chat,
    chatSingleStep,
    chatWithTools,
    listModels,
    execute,
    createAgent,
  }
}
